import React, { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"

const fmtDate = (val, withTime = false) => {
  if (!val) return ""
  const d = new Date(val)
  if (Number.isNaN(d.getTime())) return ""
  const date = d.toLocaleDateString("ru-RU")
  if (!withTime) return date
  return `${date} ${d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}`
}

const moduleLabel = (key) =>
  ({ hotel: "Отель", transport: "Транспорт", transfer: "Трансфер", insurance: "Страх" })[key] || key

const cleanColor = (val) => (val ? String(val).trim() : "")

const VISIBLE_CLIENTS = 2

// ── Утилиты ─────────────────────────────────────────────────────────────────

const fmt = (val) => {
  if (val === null || val === undefined) return ""
  if (typeof val === "boolean") return val ? "Да" : "Нет"
  if (typeof val === "object") return "" // защита от крэша
  return String(val)
}

const fmtMoney = (amount, currency) =>
  [amount != null ? String(amount) : null, currency].filter(Boolean).join(" ")

const paymentTypeLabels = {
  transfer: "Перевод",
  check_cash: "Нал.чек",
  bank_payment: "Банк",
  bank_card: "Карта",
  terminal: "Терминал",
  no_check_cash: "Нал.",
}

// ── Иконки ─────────────────────────────────────────────────────────────────

const IconRefresh = ({ className = "w-3 h-3" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
)

const IconTrash = ({ className = "w-3 h-3" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
)

const IconChat = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
    />
  </svg>
)

// ── Стек аватарок "кто сейчас смотрит заявку" ────────────────────────────────
// Ожидаемый формат данных: row.active_viewers = [{ id, name, avatar_url?, color? }]

const VISIBLE_AVATARS = 4
const AVATAR_COLORS = [
  "#6366f1",
  "#06b6d4",
  "#f59e0b",
  "#ec4899",
  "#10b981",
  "#8b5cf6",
  "#ef4444",
  "#14b8a6",
]

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?"

const colorFor = (key) =>
  AVATAR_COLORS[
    Math.abs(
      String(key)
        .split("")
        .reduce((a, c) => a + c.charCodeAt(0), 0)
    ) % AVATAR_COLORS.length
  ]

const ViewersStack = ({ viewers }) => {
  if (!viewers?.length) return null
  const shown = viewers.slice(0, VISIBLE_AVATARS)
  const extra = viewers.length - shown.length

  return (
    <div className="flex items-center" title={viewers.map((v) => v.name).join(", ")}>
      {shown.map((v, i) => (
        <div
          key={v.id ?? i}
          style={{
            backgroundColor: v.color || colorFor(v.id ?? v.name),
            marginLeft: i === 0 ? 0 : -8,
            zIndex: shown.length - i,
          }}
          className="relative w-6 h-6 rounded-full border-2 border-white flex items-center justify-center
            text-[9px] font-bold text-white shadow-sm"
        >
          {v.avatar_url ? (
            <img
              src={v.avatar_url}
              alt={v.name}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            initials(v.name)
          )}
        </div>
      ))}
      {extra > 0 && (
        <div
          style={{ marginLeft: -8 }}
          className="relative w-6 h-6 rounded-full border-2 border-white bg-gray-200
            flex items-center justify-center text-[9px] font-bold text-gray-600 shadow-sm"
        >
          +{extra}
        </div>
      )}
    </div>
  )
}

const StatusBadge = ({ statusId, statusName, statusTabs }) => {
  const tab = statusTabs?.find((s) => String(s.id) === String(statusId))
  const bg = cleanColor(tab?.background) || "#f3f4f6"
  const fg = cleanColor(tab?.color) || "#374151"
  const label = statusName || tab?.label || String(statusId)

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium border"
      style={{ backgroundColor: bg, color: fg, borderColor: fg + "33" }}
    >
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: fg }} />
      {label}
    </span>
  )
}

const DetailRow = ({ label, children }) => {
  if (children === null || children === undefined || children === "") return null
  return (
    <div className="flex gap-1.5">
      <span className="text-gray-400 font-medium shrink-0">{label}:</span>
      <span className="text-gray-700">{children}</span>
    </div>
  )
}

// ── Кружок прогресса оплаты (перенесено из RequestsTable) ───────────────────

const ProgressCircle = ({ percent, label, size = 32 }) => {
  const r = (size - 4) / 2
  const circ = 2 * Math.PI * r
  const pct = Math.min(Math.max(percent || 0, 0), 100)
  const dash = (pct / 100) * circ
  const color = pct >= 99 ? "#22c55e" : pct > 20 ? "#f59e0b" : "#ef4444"

  return (
    <div className="flex flex-col items-center gap-0.5" title={`${label}: ${pct}%`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={3} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={3}
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="7"
          fill={color}
          fontWeight="600"
        >
          {Math.round(pct)}%
        </text>
      </svg>
      <span className="text-[9px] text-gray-400 leading-none max-w-[40px] text-center truncate">
        {label}
      </span>
    </div>
  )
}

// ── Связанные заявки (перенесено из RequestsTable) ───────────────────────────

const RelatedBundlesCell = ({ bundles }) => {
  if (!bundles?.length) return null
  return (
    <div className="flex flex-wrap gap-1">
      {bundles.map((b) => (
        <span
          key={b.id}
          className="text-[11px] text-blue-600 font-medium bg-blue-50 px-1.5 py-0.5 rounded"
        >
          #{b.order || b.reservation || b.id}
        </span>
      ))}
    </div>
  )
}

// ── Основной компонент ───────────────────────────────────────────────────────
const MobileRequestCard = ({
  row,
  statusTabs = [],
  selected,
  onToggle,
  onEdit,
  onDelete,
  onRefresh,
}) => {
  const [expanded, setExpanded] = useState(false)
  const [allClients, setAllClients] = useState(false)

  // Туристы: col=client → row.clients; col=tourist_name → row.tourist_names (строка)
  const clientNames = (row.clients || [])
    .map((c) => `${c.name ?? ""} ${c.surname ?? ""}`.trim())
    .filter(Boolean)

  const hasMoreClients = clientNames.length > VISIBLE_CLIENTS
  const shownClients = allClients ? clientNames : clientNames.slice(0, VISIBLE_CLIENTS)

  // Для row_style (цветовая система статусов бэкенда)
  const rowBg = row.row_style?.background
  const rowFg = row.row_style?.color

  const pbt = row.payments_by_type

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      style={rowBg ? { backgroundColor: rowBg, color: rowFg } : undefined}
      className={`rounded-2xl border transition-all
        ${selected ? "border-blue-300 shadow-md shadow-blue-100" : "border-gray-200 shadow-sm"}
        ${!rowBg ? "bg-white" : ""}`}
    >
      {/* ── Заголовок карточки ── */}
      <div className="flex items-start gap-3 p-4">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4 flex-shrink-0"
        />

        <div className="flex-1 min-w-0">
          {/* Номер (кнопка) + чат-иконка со счётчиком + статус */}
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                window.location.href = `/book/bundle/edit/${row.id}`
              }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg
                bg-gradient-to-br from-rose-400 to-orange-400 text-white text-xs font-bold
                shadow-sm active:scale-95 transition-all"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              #{row.display_nr ?? row.order ?? row.id}
            </button>

            {row.unread_comments > 0 && (
              <div
                className="relative inline-flex cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  window.EventBus.emit("modal:chatbox:open", {
                    locale: I18n.locale,
                    bundleId: row.id,
                  })
                }}
              >
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-50 text-blue-600">
                  <IconChat />
                </span>
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[15px] h-[15px] px-0.5 rounded-full bg-blue-500 text-white text-[8px] font-bold leading-none">
                  {row.unread_comments}
                </span>
              </div>
            )}

            <StatusBadge
              statusId={row.status_id}
              statusName={row.status_name}
              statusTabs={statusTabs}
            />

            <ViewersStack viewers={row.active_viewers} />
          </div>

          {/* Флаги */}
          {(row.onrequest ||
            row.check_mode ||
            row.prebooking ||
            row.competitor_price_request ||
            row.not_show_price ||
            row.is_archive ||
            row.is_delete) && (
            <div className="flex flex-wrap gap-1 mb-1.5">
              {row.onrequest && (
                <span className="px-1.5 py-0.5 rounded text-[10px] bg-yellow-50 text-yellow-700 border border-yellow-200">
                  По запросу
                </span>
              )}
              {row.check_mode && (
                <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-50 text-purple-700 border border-purple-200">
                  Режим проверки
                </span>
              )}
              {row.prebooking && (
                <span className="px-1.5 py-0.5 rounded text-[10px] bg-sky-50 text-sky-700 border border-sky-200">
                  Предбронирование
                </span>
              )}
              {row.competitor_price_request && (
                <span className="px-1.5 py-0.5 rounded text-[10px] bg-orange-50 text-orange-700 border border-orange-200">
                  Запрос цены конкурента
                </span>
              )}
              {row.not_show_price && (
                <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600">
                  Цена скрыта
                </span>
              )}
              {row.is_archive && (
                <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600">
                  Архив
                </span>
              )}
              {row.is_delete && (
                <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-50 text-red-600">
                  Удалён
                </span>
              )}
            </div>
          )}

          {/* Туристы из col=client */}
          {clientNames.length > 0 && (
            <div className="text-xs text-gray-800 font-medium leading-relaxed">
              {shownClients.map((name, i) => (
                <div key={i}>{name}</div>
              ))}
              {hasMoreClients && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setAllClients((v) => !v)
                  }}
                  className="mt-0.5 text-[11px] font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                >
                  {allClients ? "Скрыть" : `Ещё ${clientNames.length - VISIBLE_CLIENTS}`}
                </button>
              )}
            </div>
          )}

          {/* Туристы из col=tourist_name (строка от бэкенда) */}
          {!clientNames.length && row.tourist_names && (
            <div className="text-xs text-gray-800 font-medium">
              {row.tourist_names}
              {row.tourist_count != null && (
                <span className="text-gray-400 font-normal"> · {row.tourist_count} чел.</span>
              )}
            </div>
          )}

          {/* Направление + даты */}
          <p className="text-xs text-gray-500 mt-1">
            {[row.state_name, row.resort_name].filter(Boolean).join(", ")}
            {(row.state_name || row.resort_name) && (row.from_date || row.to_date) ? " · " : ""}
            {fmtDate(row.from_date)}
            {row.from_date && row.to_date ? " — " : ""}
            {fmtDate(row.to_date)}
            {row.nights ? ` · ${row.nights} н.` : ""}
          </p>

          {/* Сумма — всегда видна */}
          {row.company_sum != null && (
            <p className="text-xs font-semibold text-gray-800 mt-1">
              {row.company_sum} {row.valute_name}
            </p>
          )}
        </div>

        {/* Кнопки действий */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => {
              window.location.href = `/book/bundle/log/${row.id}`
            }}
            className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors"
          >
            <IconRefresh className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
          >
            <IconTrash className="w-4 h-4" />
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors"
          >
            <motion.svg
              animate={{ rotate: expanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </motion.svg>
          </button>
        </div>
      </div>

      {/* ── Раскрытые детали ── */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: "hidden" }}
          >
            <div className="px-4 pb-4 pt-0 border-t border-gray-100 space-y-1.5 text-xs">
              {/* Отель */}
              <DetailRow label="Отель">
                {[row.hotel_name, row.food_name, row.type_room_name].filter(Boolean).join(" · ") ||
                  null}
              </DetailRow>
              {row.hotel_allocated && (
                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-700">
                  Размещён
                </span>
              )}

              {/* Поставщики */}
              {(row.providers || []).length > 0 && (
                <DetailRow label="Поставщики">
                  {row.providers
                    .map(
                      (p) =>
                        `${moduleLabel(p.module_key)}: ${p.provider}${p.order ? ` — ${p.order}` : ""}${
                          p.status === "is_canceled" ? " (отменён)" : ""
                        }`
                    )
                    .join(" · ")}
                </DetailRow>
              )}

              {/* Транспорт */}
              {(row.transport_charter_depart || row.transport_charter_retur) && (
                <DetailRow label="Транспорт">
                  {[
                    row.transport_charter_depart &&
                      `↑ ${row.transport_charter_depart}${row.transport_from_date ? ` (${fmtDate(row.transport_from_date)})` : ""}`,
                    row.transport_charter_retur &&
                      `↓ ${row.transport_charter_retur}${row.transport_to_date ? ` (${fmtDate(row.transport_to_date)})` : ""}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </DetailRow>
              )}
              {row.transport_badge && (
                <span
                  className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium
                    ${
                      row.transport_badge.type === "canceled"
                        ? "bg-red-50 text-red-700"
                        : "bg-amber-50 text-amber-700"
                    }`}
                >
                  {row.transport_badge.label}
                </span>
              )}

              {/* Рейсы */}
              {(row.flight_depart_id > 0 || row.flight_return_id > 0) && (
                <DetailRow label="Рейсы">
                  {[
                    row.flight_depart_id > 0 && `↑ #${row.flight_depart_id}`,
                    row.flight_return_id > 0 && `↓ #${row.flight_return_id}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </DetailRow>
              )}

              {/* Места */}
              {(row.depart_places != null || row.retur_places != null) && (
                <DetailRow label="Места">
                  {[
                    row.depart_places != null && `↑ ${row.depart_places}`,
                    row.retur_places != null && `↓ ${row.retur_places}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </DetailRow>
              )}

              {/* Бронирование / договор */}
              <DetailRow label="Бронь">
                {[row.reservation, row.contract_nr ? `Договор: ${row.contract_nr}` : null]
                  .filter(Boolean)
                  .join(" · ") || null}
              </DetailRow>

              {/* Компания */}
              <DetailRow label="Компания">
                {row.company_name}
                {row.company_type_name && (
                  <span
                    className="inline-block ml-1.5 px-1.5 py-0.5 rounded text-[10px] align-middle"
                    style={{
                      backgroundColor: row.company_type_color || "#f3f4f6",
                      color: "#374151",
                    }}
                  >
                    {row.company_type_name}
                  </span>
                )}
              </DetailRow>

              {/* Агент */}
              {row.user_name && (
                <DetailRow label="Агент">
                  {row.user_name}
                  {row.user_email ? ` · ${row.user_email}` : ""}
                </DetailRow>
              )}

              {/* Модератор */}
              <DetailRow label="Модератор">{row.moderator_name || null}</DetailRow>

              {/* Финансы (клиент/агентство) */}
              {row.agency_amount != null && (
                <DetailRow label="Сумма агентства">{fmt(row.agency_amount)}</DetailRow>
              )}
              {row.agency_paid != null && (
                <DetailRow label="Оплачено">
                  {fmt(row.agency_paid)}
                  {row.agency_paid_pct != null && (
                    <span className="text-gray-400"> ({row.agency_paid_pct}%)</span>
                  )}
                </DetailRow>
              )}
              {row.agency_debt != null && (
                <DetailRow label="Долг агентства">
                  <span className={row.agency_debt > 0 ? "text-red-500" : "text-emerald-600"}>
                    {fmt(row.agency_debt)}
                  </span>
                </DetailRow>
              )}
              {row.client_costs != null && (
                <DetailRow label="Затраты клиента">{fmt(row.client_costs)}</DetailRow>
              )}

              {/* Финансы (оператор/прибыль) */}
              {row.operator_debt != null && (
                <DetailRow label="Долг оператору">
                  <span className={row.operator_debt > 0 ? "text-red-500" : "text-emerald-600"}>
                    {fmt(row.operator_debt)}
                  </span>
                </DetailRow>
              )}
              {row.profit != null && (
                <DetailRow label="Прибыль">
                  <span className={row.profit < 0 ? "text-red-500" : "text-emerald-700"}>
                    {fmt(row.profit)}
                  </span>
                </DetailRow>
              )}
              {row.transport_profit != null && (
                <DetailRow label="Прибыль (транспорт)">
                  <span className={row.transport_profit < 0 ? "text-red-500" : "text-emerald-700"}>
                    {fmt(row.transport_profit)}
                  </span>
                </DetailRow>
              )}

              {/* Затраты на поставщиков (операторские) */}
              {(row.total != null || row.paid != null) && (
                <DetailRow label="Затраты (оператор)">
                  {fmt(row.total)}
                  {row.paid != null && (
                    <span className="text-gray-400"> · Опл: {fmt(row.paid)}</span>
                  )}
                </DetailRow>
              )}
              {row.hotel_operator_costs && (
                <DetailRow label="Затраты (отель)">
                  {fmt(row.hotel_operator_costs.total)}
                  {row.hotel_operator_costs.paid != null && (
                    <span className="text-gray-400">
                      {" "}
                      · Опл: {fmt(row.hotel_operator_costs.paid)}
                    </span>
                  )}
                </DetailRow>
              )}
              {row.transfer_operator_costs && (
                <DetailRow label="Затраты (трансфер)">
                  {fmt(row.transfer_operator_costs.total)}
                  {row.transfer_operator_costs.paid != null && (
                    <span className="text-gray-400">
                      {" "}
                      · Опл: {fmt(row.transfer_operator_costs.paid)}
                    </span>
                  )}
                </DetailRow>
              )}

              {/* Прогресс оплаты */}
              {(row.payment_progress || []).length > 0 && (
                <div>
                  <span className="text-gray-400 font-medium">Прогресс оплаты:</span>
                  <div className="flex items-start gap-3 flex-wrap mt-1">
                    {row.payment_progress.map((c) => (
                      <ProgressCircle key={c.key} percent={c.percent} label={c.label} />
                    ))}
                  </div>
                </div>
              )}

              {/* Платежи от клиента по типу */}
              {pbt && Object.values(pbt).some((v) => v > 0) && (
                <div>
                  <span className="text-gray-400 font-medium">Оплата от клиента:</span>
                  <div className="mt-0.5 space-y-0.5">
                    {Object.entries(pbt)
                      .filter(([, v]) => v > 0)
                      .map(([k, v]) => (
                        <div key={k}>
                          <span className="text-gray-400">{paymentTypeLabels[k] ?? k}: </span>
                          <span className="text-gray-700 font-medium">{v}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Платежи агентства: локальные суммы / курс / примечания */}
              <DetailRow label="Оплачено (локально)">{fmt(row.sum_paid_local)}</DetailRow>
              <DetailRow label="Курс оплаты">{fmt(row.payment_rates)}</DetailRow>
              <DetailRow label="Примечания к оплате">{fmt(row.payment_notes)}</DetailRow>

              {/* Статус размещения */}
              <DetailRow label="Размещение">{row.room_status_name || null}</DetailRow>

              {/* Комментарий */}
              <DetailRow label="Комментарий">{row.comment || row.comment_admin || null}</DetailRow>

              {/* Заметки по услугам */}
              {row.service_notes &&
                typeof row.service_notes === "object" &&
                Object.entries(row.service_notes).map(([k, v]) =>
                  v ? (
                    <DetailRow key={k} label={moduleLabel(k)}>
                      {v}
                    </DetailRow>
                  ) : null
                )}

              {/* Связанные заявки */}
              {(row.related_bundles || []).length > 0 && (
                <div>
                  <span className="text-gray-400 font-medium">Связанные заявки:</span>
                  <div className="mt-1">
                    <RelatedBundlesCell bundles={row.related_bundles} />
                  </div>
                </div>
              )}

              {/* Дата запроса */}
              <DetailRow label="Дата запроса">{fmtDate(row.created_at, true)}</DetailRow>

              {/* Дата предбронирования */}
              {row.prebooking_date && (
                <DetailRow label="Пребук">{fmtDate(row.prebooking_date)}</DetailRow>
              )}

              {/* Источник */}
              <DetailRow label="Источник">{row.referer || null}</DetailRow>

              {/* Непрочитанные сообщения */}
              {row.unread_comments > 0 && (
                <DetailRow label="Чат">
                  <span className="text-blue-600 font-semibold">{row.unread_comments} новых</span>
                  {row.last_unread_at && (
                    <span className="text-gray-400 ml-1">
                      · {fmtDate(row.last_unread_at, true)}
                    </span>
                  )}
                </DetailRow>
              )}

              {/* Кто сейчас смотрит */}
              {row.active_viewers?.length > 0 && (
                <DetailRow label="Сейчас смотрят">
                  {row.active_viewers
                    .map((v) => v.name)
                    .filter(Boolean)
                    .join(", ")}
                </DetailRow>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default MobileRequestCard
